import { CurrencyPipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { STChange, STColumn } from '@delon/abc/st';
import { SettingsService, _HttpClient } from '@delon/theme';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { finalize, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { ReportingService } from 'src/app/core/util/reporting.service';
import {
  REVERSE_ACL_MAP_SENDER_TRANSACTION_TYPE
} from 'src/app/shared-feature/components/email-status-modal/send-email-mapping';

import { BatchEmailMode } from '../../actions/batch-email/batch-email.component';

/** ACL resource keys vs reporting `GetByType` voucher ids (must match list `reportTypes`, not always `{aclKey}-voucher`). */
const ACL_KEY_TO_VOUCHER_REPORT_TYPE: Record<string, string> = {
  aror: 'or-voucher',
  appv: 'pv-voucher'
};

type BatchEmailRow = {
  id: string;
  docCode: string;
  recipientName: string;
  recipientEmails: string[];
  recipientEmail: string;
  subject: string;
  message: string;
  senderEmail: string;
  companyName: string;
  emailOptions: string[];
  raw: any;
  checked?: boolean;
  excluded?: boolean;
  error?: string;
  paymentData?: any;
};

@Component({
  selector: 'app-batch-email-modal',
  templateUrl: './batch-email-modal.component.html',
  styleUrls: ['./batch-email-modal.component.less'],
  standalone: false,
  providers: [CurrencyPipe]
})
export class BatchEmailModalComponent implements OnChanges {
  @Input() visible = false;
  @Input() mode: BatchEmailMode | null = null;
  @Input() selectedRows: any[] = [];
  @Input() aclKey: string | undefined;
  @Input() country: string | undefined;
  @Output() readonly close = new EventEmitter<void>();

  rows: BatchEmailRow[] = [];
  selectedBatchRows: BatchEmailRow[] = [];
  activeRow: BatchEmailRow | null = null;
  loading = false;
  sending = false;
  reportUrl: string | null = null;
  hasReportFormats = false;
  columns: STColumn[] = [
    { title: '', index: 'id', type: 'checkbox', width: 40 },
    { title: 'Doc#', render: 'docTpl', width: 110 },
    { title: 'To', render: 'toTpl', width: 140 },
    { title: 'Email', render: 'emailTpl', width: 280 }
  ];
  reportFormats: any[] = [];
  batchSelectedReport: string | null = null;
  batchSubject = '';
  batchMessage = '';
  batchSendCopy = false;
  batchIncludeAttachment = true;

  constructor(
    private http: _HttpClient,
    private reporting: ReportingService,
    private msg: NzMessageService,
    private notification: NzNotificationService,
    private settings: SettingsService,
    private currency: CurrencyPipe,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['visible'] || changes['mode'] || changes['selectedRows']) &&
      this.visible &&
      this.mode
    ) {
      this.loadRows();
    }
  }

  get allowPayment(): boolean {
    const key = (this.aclKey || '').toLowerCase();
    return key === 'sinv' || key === 'inv';
  }

  selectMode(mode: BatchEmailMode) {
    if (mode === 'payment' && !this.allowPayment) return;
    this.mode = mode;
    this.loadRows();
  }

  backToSelection() {
    if (this.sending) return;
    this.mode = null;
    this.rows = [];
  }

  handleCancel(): void {
    if (this.sending) return;
    this.close.emit();
  }

  handleSubmit(): void {
    if (!this.rows.length) return;
    this.persistActiveRowEdits();

    // Resolve current selection (checkbox)
    this.selectedBatchRows =
      this.rows.filter(r => !!r.checked) || this.selectedBatchRows || [];

    if (!this.selectedBatchRows.length) {
      this.msg.error('Please select at least one transaction.');
      return;
    }

    const selected = this.selectedBatchRows;

    if (selected.some(r => !!r.error)) {
      this.msg.error('Some selected transactions cannot be emailed.');
      return;
    }

    // Ensure all recipient edits are synced before validation/payload build
    selected.forEach(r => this.updateRowRecipientEmails(r));

    // Normalize + validate recipient emails based on latest user input
    const invalidEmpty: string[] = [];
    const invalidFormat: string[] = [];
    selected.forEach(row => {
      row.recipientEmails = this.normalizeEmailArray(row.recipientEmails);
      row.recipientEmail = row.recipientEmails.join(',');
      if (!row.recipientEmails.length) {
        invalidEmpty.push(row.docCode);
        return;
      }
      if (!this.validateEmailArray(row.recipientEmails)) {
        invalidFormat.push(row.docCode);
      }
    });
    if (invalidEmpty.length || invalidFormat.length) {
      if (invalidEmpty.length) {
        this.msg.error(
          `Recipient email is empty for: ${invalidEmpty.slice(0, 8).join(', ')}${
            invalidEmpty.length > 8 ? '...' : ''
          }`
        );
      } else {
        this.msg.error(
          `Recipient email is invalid for: ${invalidFormat.slice(0, 8).join(', ')}${
            invalidFormat.length > 8 ? '...' : ''
          }`
        );
      }
      return;
    }
    this.batchSubject = this.batchSubject;

    this.sending = true;
    const notification = this.notification.create(
      'info',
      'Sending batch email...',
      'Please wait',
      { nzDuration: 0 }
    );

    const request$ =
      this.mode === 'payment'
        ? this.sendBatchPayment()
        : this.sendBatchNotification();

    request$
      .pipe(
        finalize(() => {
          this.notification.remove(notification.messageId);
          this.sending = false;
        })
      )
      .subscribe({
        next: result => {
          const success = result?.successCount ?? 0;
          const failed = result?.failedCount ?? 0;
          this.msg.success(
            `Batch email completed. Success: ${success}, Failed: ${failed}.`
          );
          this.close.emit();
        },
        error: () => {
          this.msg.error('Failed to send batch email. Please try again.');
        }
      });
  }

  loadRows(): void {
    if (!this.mode) return;
    this.loading = true;
    this.rows = [];
    this.activeRow = null;
    this.batchSubject = '';
    this.batchMessage = '';
    this.batchSendCopy = false;
    this.batchIncludeAttachment = true;

    this.loadReportFormats()
      .pipe(
        mergeMap(() => this.buildRows()),
        catchError(() => {
          this.msg.error('Failed to load Batch Email. Please try again.');
          return of([] as BatchEmailRow[]);
        }),
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(rows => {
        const excludedRows = (rows ?? []).filter(r => !!r.excluded);
        if (excludedRows.length) {
          this.msg.warning(
            `Excluded ${excludedRows.length} cancelled transaction${
              excludedRows.length > 1 ? 's' : ''
            } from batch email.`
          );
        }

        const availableRows = (rows ?? []).filter(r => !r.excluded);
        if (!availableRows.length) {
          this.msg.warning('Please select at least one non-cancelled transaction.');
          this.mode = null;
          this.rows = [];
          this.selectedBatchRows = [];
          this.activeRow = null;
          this.syncEditorFromActiveRow();
          this.cdr.detectChanges();
          return;
        }

        // Default selection: all rows (same behavior as before checkbox column existed)
        this.rows = availableRows.map(r => ({ ...r, checked: true }));
        this.selectedBatchRows = this.rows.slice();
        this.activeRow = this.rows.length ? this.rows[0] : null;
        this.syncEditorFromActiveRow();
        this.cdr.detectChanges();
      });
  }

  change(e: STChange): void {
    if (e.type !== 'checkbox') return;
    if (this.sending) return;

    if (e.checkbox) {
      this.selectedBatchRows = e.checkbox as BatchEmailRow[];
      const selectedIds = new Set(this.selectedBatchRows.map(r => r.id));
      this.rows = this.rows.map(r => ({ ...r, checked: selectedIds.has(r.id) }));
    }

    if (this.activeRow && !this.activeRow.checked) {
      const next = this.rows.find(r => r.checked) ?? null;
      this.activeRow = next;
      this.syncEditorFromActiveRow();
    }
  }

  private loadReportFormats() {
    const reportType = this.getReportType();
    if (!reportType) return of(null);

    return this.http
      .get(`${this.reporting.reportServerMirrorUrl}/api/ReportFiles/GetByType`, {
        reportType
      })
      .pipe(
        mergeMap((res: any[]) => {
          this.reportFormats = res ?? [];
          this.reportUrl = res?.[0]?.reportUrl ?? null;
          this.batchSelectedReport = this.reportUrl;
          this.hasReportFormats = this.reportFormats.length > 0;
          if (!this.hasReportFormats) {
            this.batchIncludeAttachment = false;
          }
          return of(this.reportUrl);
        }),
        catchError(() => {
          this.reportFormats = [];
          this.reportUrl = null;
          this.batchSelectedReport = null;
          this.hasReportFormats = false;
          this.batchIncludeAttachment = false;
          return of(null);
        })
      );
  }

  private buildRows() {
    const baseRows = (this.selectedRows || [])
      .filter(row => !this.isRowCancelled(row))
      .map(row => {
      return {
        id: this.getRowId(row),
        docCode: this.getRowDocCode(row),
        recipientName: '',
        recipientEmails: [],
        recipientEmail: '',
        subject: '',
        message: '',
        senderEmail: '',
        companyName: '',
        emailOptions: [],
        raw: row
      } as BatchEmailRow;
    });

    if (!baseRows.length) {
      return of([] as BatchEmailRow[]);
    }

    if (this.mode === 'payment') {
      return this.buildPaymentRows(baseRows);
    }
    return this.buildNotificationRows(baseRows);
  }

  private buildRow(row: BatchEmailRow) {
    if (this.mode === 'payment') {
      return this.buildPaymentRow(row);
    }
    return this.buildNotificationRow(row);
  }

  private buildNotificationRow(row: BatchEmailRow) {
    const senderType = this.getSenderType();
    if (!senderType) {
      return of({
        ...row,
        error: 'Unsupported transaction type'
      });
    }

    const request = {
      type: senderType,
      transactionId: row.id,
      option: 'email',
      // Notification batch email must default to Billing email (not E-Invoice email)
      useEInvoiceEmail: false
    };

    return this.http.post('api/transactionSender/info?_silent=true', request).pipe(
      mergeMap((res: any) => {
        const emailOptions = this.normalizeEmailArray([
          ...(this.parseEmailList(res.receipientEmail) || []),
          ...(this.parseEmailList(res.ccList) || [])
        ]);
        // Requirement: display all emails (primary + secondary) by default
        const recipientEmails = emailOptions.slice();
        return of({
          ...row,
          recipientName: res.receipientName ?? '',
          recipientEmail: recipientEmails.join(','),
          recipientEmails,
          subject: res.subject ?? '',
          message: res.message ?? '',
          senderEmail: res.email ?? '',
          companyName: res.companyName ?? '',
          emailOptions,
          docCode: res.docCode ?? row.docCode
        });
      }),
      catchError(() =>
        of({
          ...row,
          error: 'Failed to load recipient'
        })
      )
    );
  }


  private buildPaymentRow(row: BatchEmailRow) {
    const docType = this.getPaymentDocType();
    if (!docType) {
      return of({
        ...row,
        error: 'Payment email not supported'
      });
    }

    const request = {
      option: 'email',
      docId: row.id,
      docType,
      amount: this.getRowAmount(row.raw),
      transactionType: this.batchSelectedReport ?? this.reportUrl ?? '',
      userPrimaryDb: true
    };

    return this.http.post('api/PaymentPortal/GenerateLink', request).pipe(
      mergeMap((res: any) => {
        const emailOptions = this.normalizeEmailArray([
          ...(this.parseEmailList(res.to) || []),
          ...(this.parseEmailList(res.ccEmails) || [])
        ]);
        // Requirement: display all emails (primary + secondary) by default
        const recipientEmails = emailOptions.slice();
        const link = this.generatePaymentLink(res.linkId);
        return of({
          ...row,
          recipientName: res.customerName ?? '',
          recipientEmail: recipientEmails.join(','),
          recipientEmails,
          subject: res.subject ?? '',
          message: res.message ?? '',
          senderEmail: res.from ?? '',
          companyName: res.companyName ?? '',
          emailOptions,
          paymentData: {
            ...res,
            link
          }
        });
      }),
      catchError(() =>
        of({
          ...row,
          error: 'Failed to load payment email'
        })
      )
    );
  }

  private buildNotificationRows(baseRows: BatchEmailRow[]) {
    const senderType = this.getSenderType();
    if (!senderType) {
      return of(
        baseRows.map(row => ({
          ...row,
          error: 'Unsupported transaction type'
        }))
      );
    }

    const requests = baseRows.map(row => ({
      type: senderType,
      transactionId: row.id,
      option: 'email',
      // Notification batch email must default to Billing email (not E-Invoice email)
      useEInvoiceEmail: false
    }));

    return this.http
      .post('api/transactionSender/BatchInfo?_silent=true', requests)
      .pipe(
        mergeMap((res: any[]) => of(this.mapNotificationRows(baseRows, res))),
        catchError(() =>
          of(
            baseRows.map(row => ({
              ...row,
              error: 'Failed to load recipient'
            }))
          )
        )
      );
  }

  private mapNotificationRows(baseRows: BatchEmailRow[], responses: any[]) {
    const responseMap = new Map<string, any>();
    (responses || []).forEach(item => {
      const key = item?.transactionId ?? item?.TransactionId ?? '';
      if (key) responseMap.set(String(key), item);
    });

    return baseRows.map(row => {
      const result = responseMap.get(String(row.id));
      if (!result) {
        return { ...row, error: 'Failed to load recipient' };
      }
      if (result.excluded === true || result.Excluded === true) {
        return {
          ...row,
          excluded: true,
          error: result.error ?? result.Error ?? 'Transaction is cancelled.'
        };
      }
      if (result.success === false || result?.Success === false) {
        return { ...row, error: result.error ?? result.Error ?? 'Failed to load recipient' };
      }

      const info = result.info ?? result.Info;
      if (!info) {
        return { ...row, error: 'Failed to load recipient' };
      }

      const emailOptions = this.normalizeEmailArray([
        ...(this.parseEmailList(info.receipientEmail ?? info.ReceipientEmail) || []),
        ...(this.parseEmailList(info.ccList ?? info.CcList) || [])
      ]);
      // Requirement: display all emails (primary + secondary) by default
      const recipientEmails = emailOptions.slice();
      return {
        ...row,
        recipientName: info.receipientName ?? info.ReceipientName ?? '',
        recipientEmail: recipientEmails.join(','),
        recipientEmails,
        subject: info.subject ?? info.Subject ?? '',
        message: info.message ?? info.Message ?? '',
        senderEmail: info.email ?? info.Email ?? '',
        companyName: info.companyName ?? info.CompanyName ?? '',
        emailOptions,
        docCode: info.docCode ?? info.DocCode ?? row.docCode
      };
    });
  }

  private buildPaymentRows(baseRows: BatchEmailRow[]) {
    const docType = this.getPaymentDocType();
    if (!docType) {
      return of(
        baseRows.map(row => ({
          ...row,
          error: 'Payment email not supported'
        }))
      );
    }

    const requests = baseRows.map(row => ({
      option: 'email',
      docId: row.id,
      docType,
      amount: this.getRowAmount(row.raw),
      transactionType: this.batchSelectedReport ?? this.reportUrl ?? '',
      userPrimaryDb: true
    }));

    return this.http
      .post('api/PaymentPortal/BatchGenerateLink', requests)
      .pipe(
        mergeMap((res: any[]) => of(this.mapPaymentRows(baseRows, res))),
        catchError(() =>
          of(
            baseRows.map(row => ({
              ...row,
              error: 'Failed to load payment email'
            }))
          )
        )
      );
  }

  private mapPaymentRows(baseRows: BatchEmailRow[], responses: any[]) {
    const responseMap = new Map<string, any>();
    (responses || []).forEach(item => {
      const key = item?.docId ?? item?.DocId ?? '';
      if (key) responseMap.set(String(key), item);
    });

    return baseRows.map(row => {
      const result = responseMap.get(String(row.id));
      if (!result) {
        return { ...row, error: 'Failed to load payment email' };
      }
      if (result.success === false || result?.Success === false) {
        return { ...row, error: result.error ?? result.Error ?? 'Failed to load payment email' };
      }

      const res = result.response ?? result.Response;
      if (!res) {
        return { ...row, error: 'Failed to load payment email' };
      }

      const emailOptions = this.normalizeEmailArray([
        ...(this.parseEmailList(res.to ?? res.To) || []),
        ...(this.parseEmailList(res.ccEmails ?? res.CcEmails) || [])
      ]);
      // Requirement: display all emails (primary + secondary) by default
      const recipientEmails = emailOptions.slice();
      const link = this.generatePaymentLink(res.linkId ?? res.LinkId);
      return {
        ...row,
        recipientName: res.customerName ?? res.CustomerName ?? '',
        recipientEmail: recipientEmails.join(','),
        recipientEmails,
        subject: res.subject ?? res.Subject ?? '',
        message: res.message ?? res.Message ?? '',
        senderEmail: res.from ?? res.From ?? '',
        companyName: res.companyName ?? res.CompanyName ?? '',
        emailOptions,
        paymentData: {
          ...res,
          link
        }
      };
    });
  }


  private sendBatchNotification() {
    const sendRows = (this.rows || []).filter(r => !!r.checked && !r.error);
    const models = sendRows.map(row => ({
      docCode: row.docCode,
      docType: this.aclKey,
      senderName: row.companyName,
      senderEmail: '',
      receipientEmails: (row.recipientEmails || []).slice(),
      receipientEmail: (row.recipientEmails || []).join(','),
      subject: row.subject,
      htmlContent: '',
      message: row.message,
      type: 1,
      filter: JSON.stringify({ selection: [row.docCode] }),
      usePrimaryDb: true,
      reportUrl: this.batchSelectedReport ?? this.reportUrl ?? '',
      cc: '',
      bcc: ''
    }));

    return this.http
      .post(
        'api/EmailSender/SendDocumentBatch',
        models
      )
      .pipe(
        mergeMap((res: any) =>
          of(this.normalizeBatchResult(res, models.length))
        ),
        catchError(() =>
          of({ successCount: 0, failedCount: models.length })
        )
      );
  }

  private sendBatchPayment() {
    const sendRows = (this.rows || []).filter(r => !!r.checked && !r.error);
    const models = sendRows
      .filter(row => row.paymentData)
      .map(row => {
        const data = row.paymentData;
        const htmlContent = this.buildPaymentHtml(row.message, data);
        const message = this.buildPaymentMessage(row.message, data);

        return {
          ...data,
          subject: row.subject,
          attachmentType: this.batchSelectedReport ?? this.reportUrl ?? '',
          htmlContent,
          reportUrl: this.batchSelectedReport ?? this.reportUrl ?? '',
          filter: JSON.stringify({ selection: [row.docCode] }),
          userPrimaryDb: true,
          to: (row.recipientEmails || []).join(','),
          cc: '',
          bcc: '',
          sendCopy: this.batchSendCopy,
          includeAttachment: this.batchIncludeAttachment,
          message
        };
      });

    return this.http
      .post(
        'api/EmailSender/SendPaymentInvoiceBatch',
        models
      )
      .pipe(
        mergeMap((res: any) => of(this.normalizeBatchResult(res, models.length))),
        catchError(() =>
          of({ successCount: 0, failedCount: models.length })
        )
      );
  }

  private normalizeBatchResult(res: any, total: number) {
    const payload = res?.data ?? res?.Data ?? res;
    const successCount =
      payload?.successCount ??
      payload?.SuccessCount ??
      res?.successCount ??
      res?.SuccessCount ??
      res?.success ??
      res?.Success ??
      0;
    const failedCount =
      payload?.failedCount ??
      payload?.FailedCount ??
      res?.failedCount ??
      res?.FailedCount ??
      (total - Number(successCount || 0));
    return { successCount: Number(successCount || 0), failedCount: Number(failedCount || 0) };
  }


  private buildPaymentHtml(template: string, data: any) {
    const text = this.buildPaymentMessage(template, data)
      .replaceAll('\n\n', '</div><br><div>')
      .replaceAll('\n', '</div><div>');
    return `<div>${text}</div>`;
  }

  private buildPaymentMessage(template: string, data: any) {
    const link = data.link ?? '';
    return (template || '')
      .replaceAll('[ContactName]', data.contactName ?? '')
      .replaceAll(
        '[NetTotal]',
        this.currency.transform(data.netTotal, ' ') ?? ''
      )
      .replaceAll('[DueDate]', data.formattedDueDate ?? '')
      .replaceAll('[CompanyName]', data.companyName ?? '')
      .replaceAll('[InvoiceLink]', link)
      .replaceAll('[DocDate]', data.formattedDocDate ?? '')
      .replaceAll('[CustomerName]', data.customerName ?? '');
  }

  private shouldUseEInvoiceEmail(row: any): boolean {
    const country =
      this.country || this.settings.user?.['company']?.country || '';
    if (country !== 'MY') return false;

    const control = this.getEinvoiceControl(row);
    return control === 1 || control === 2;
  }

  private getSenderType(): number | null {
    const key = (this.aclKey || '').toLowerCase();
    const value = REVERSE_ACL_MAP_SENDER_TRANSACTION_TYPE[key];
    return value ?? null;
  }

  private getReportType(): string | null {
    if (!this.aclKey) return null;
    const key = this.aclKey.toLowerCase();
    return ACL_KEY_TO_VOUCHER_REPORT_TYPE[key] ?? `${key}-voucher`;
  }

  private getPaymentDocType(): string | null {
    const key = (this.aclKey || '').toLowerCase();
    if (key === 'sinv') return 'SINV';
    if (key === 'inv') return 'INV';
    return null;
  }

  private getRowId(row: any): string {
    return row?.id ?? row?.Id ?? '';
  }

  private getRowDocCode(row: any): string {
    return row?.docCode ?? row?.DocCode ?? '';
  }

  private getRowAmount(row: any): number {
    return row?.netTotalAmount ?? row?.NetTotalAmount ?? 0;
  }

  private isRowCancelled(row: any): boolean {
    return row?.isCancelled === true || row?.IsCancelled === true;
  }

  private getEinvoiceControl(row: any): number {
    return (
      row?.eInvoiceControl ??
      row?.einvoiceControl ??
      row?.EInvoiceControl ??
      row?.validationControl ??
      0
    );
  }

  validateEmailArray(emails: string[]): boolean {
    if (!emails || emails.length === 0) return true;
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emails.every(email => emailPattern.test(email.trim()));
  }

  private normalizeEmailArray(value: unknown): string[] {
    const arr = Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : [];
    const cleaned = arr
      .map(v => String(v ?? '').trim())
      .filter(v => !!v);
    // de-dupe (case-insensitive) while preserving order
    const seen = new Set<string>();
    const result: string[] = [];
    for (const e of cleaned) {
      const key = e.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      result.push(e);
    }
    return result;
  }

  private parseEmailList(value: string | string[] | null | undefined): string[] {
    if (!value) return [];
    if (Array.isArray(value)) return value.slice();
    return value
      .split(',')
      .map(email => email.trim())
      .filter(email => email);
  }

  updateRowRecipientEmails(row: BatchEmailRow) {
    // Normalize list and ensure the backing `rows` array is updated
    const normalized = this.normalizeEmailArray(row.recipientEmails);
    row.recipientEmails = normalized;
    row.recipientEmail = normalized.join(',');

    // In some table render flows, `row` can be a different object reference.
    // Ensure we persist edits back to our source array used for payload building.
    const master = this.rows.find(r => r.id === row.id);
    if (master && master !== row) {
      master.recipientEmails = normalized.slice();
      master.recipientEmail = row.recipientEmail;
    }
  }

  selectRow(row: BatchEmailRow) {
    if (this.sending || !row) return;
    if (this.activeRow?.id === row.id) return;
    this.persistActiveRowEdits();
    this.activeRow = row;
    this.syncEditorFromActiveRow();
  }

  private persistActiveRowEdits() {
    if (!this.activeRow) return;
    this.activeRow.subject = this.batchSubject;
    this.activeRow.message = this.batchMessage;
  }

  private syncEditorFromActiveRow() {
    if (!this.activeRow) {
      this.batchSubject = '';
      this.batchMessage = '';
      return;
    }
    this.batchSubject = this.activeRow.subject || '';
    this.batchMessage = this.activeRow.message || '';
  }

  private generatePaymentLink(id: string) {
    if (!id) return '';
    return `${window.location.origin}/#/full/portal/pay/${id}`;
  }
}
